import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogContent, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { AdminClockedInTherapistsModel } from '../../../../models/api/AdminClockedInTherapistsModel';
import { DashboardService } from '../../../../services/api/DashboardService';
import { TherapistService } from '../../../../services/api/TherapistService';
import { TherapistEventService } from '../../../../services/api/TherapistEventService';
import { EventService } from '../../../../services/api/EventService';
import { TherapistEventDetailsModel } from '../../../../models/api/TherapistEventDetails';
import { AssignTherapistToEventComponent } from './assign-therapist-to-event/assign-therapist-to-event.component';
import { EventTherapistDetailComponent } from './event-therapist-detail/event-therapist-detail.component';
import { EventModel } from '../../../../models/api/EventModel';
import { forkJoin, interval, startWith, Subject, takeUntil } from 'rxjs';
import { EventDeleteDialog } from '../../all-events/all-events.component';
import { HelpersModule } from '../../../../helpers/helpers';

@Component({
  selector: 'app-event-therapists',
  standalone: true,
  imports: [MatTableModule, RouterModule, MatTooltipModule, MatIconModule, MatSortModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatSelectModule, MatDialogContent, MatDialogActions, MatDialogClose, MatDialogTitle],
  templateUrl: './event-therapists.component.html',
  styleUrl: './event-therapists.component.scss'
})
export class EventTherapistsComponent implements OnInit, OnDestroy {
  therapists: TherapistEventDetailsModel[] = [];

  displayedColumns: string[] = ['image', 'firstName', 'lastName', 'actions'];
  dataSource = new MatTableDataSource<TherapistEventDetailsModel>();
  readonly dialog = inject(MatDialog);

  eventDetail: EventModel = new EventModel;

  public isLoaded: boolean = false;

  eventID: number = 0;

  constructor(
    private therapistEventService: TherapistEventService,
    private eventService: EventService,
    private snackbar: MatSnackBar,
    private route: ActivatedRoute,
  ) {
    this.eventID = Number(this.route.snapshot.paramMap.get('id'));
  }

      private destroy$ = new Subject<void>();
    
    ngOnInit(): void {
      interval(10000)
        .pipe(
          startWith(0),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (this.eventID > 0) {
          this.getTherapists();
        }
        });
    }
    
      ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }

  getTherapists() {

    forkJoin({
      eventDetail: this.eventService.getEvent(this.eventID),
      therapists: this.therapistEventService.getByEvent(this.eventID)
    }).subscribe({
      next: ({ eventDetail, therapists }) => {
        this.eventDetail = eventDetail;
        this.therapists = Array.from(
          new Map(therapists.map(t => [t.therapistId, t])).values()
        );

        this.dataSource.data = this.therapists;
        this.isLoaded = true;
      },
      error: (err) => {
        this.isLoaded = false;
      }
    });
  }


  removeTherapistFromEvent(therapistID: number) {


    const deleteDialog = this.dialog.open(RemoveTherapistEventAssociationDialog, {
      data: {
        therapistID: therapistID,
      },
      panelClass: 'delete-dialog',
    });

    deleteDialog.afterClosed().subscribe(result => {

      if (result) {
        this.therapistEventService.deleteByTherapistAndEvent(therapistID, this.eventID).subscribe({
          next: (data) => {
            this.snackbar.open('Therapist removed from event', 'Close', {
              duration: 5000,
              panelClass: "confirm-snackbar",
            });
            this.getTherapists();
          },
          error: (err) => {
          }
        })
      }

    })
  }

  editTherapistAtEvent(therapistID: number) {
    //Dialog to change the dates the therapist is working at this event
    this.dialog.open(EventTherapistDetailComponent, {
      disableClose: true,
      data: { therapistID: therapistID, eventID: this.eventID },
      panelClass: 'wh-form-dialog',
      width: '600px',
      maxHeight: '70vh',
    })
  }

  associateTherapist() {
    //Dialog to associate an existing therapist to this event
    const associateDialog = this.dialog.open(AssignTherapistToEventComponent, {
      disableClose: true,
      panelClass: 'wh-form-dialog',
      width: '600px',
      maxHeight: '70vh',
      data: {eventID: this.eventID},
    })

    associateDialog.afterClosed().subscribe(result => {
      this.getTherapists();
    })
  }


  getThumbURL(url: string | undefined | null): string | undefined | null {
    if (url == undefined || url == null)
      return url;

    return HelpersModule.cloudinaryURLForceWidth(url, 80);
  }

}

function toUtcDate(date: string | Date): Date {
  if (date instanceof Date) {
    const isoString = date.toISOString();
    return new Date(isoString);
  } else {
    return new Date(date.endsWith('Z') ? date : date + 'Z');
  }
}


@Component({
  selector: 'remove-therapist-event-association-dialog',
  templateUrl: 'remove-therapist-event-association-dialog.html',
  standalone: true,
  imports: [MatButtonModule, MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoveTherapistEventAssociationDialog {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { therapistID: number }) { }

  readonly dialogRef = inject(MatDialogRef<EventDeleteDialog>);
}
